import debounce from 'lodash/debounce';
import ls from 'localstorage-slim';
import Vue from 'vue';

function getOfferKey({amount, term, type, id = ''}) {
  return `${amount}/${term}/${type}/${id}`;
}

export default async ({ app, $api, store, query, error, $tools }) => {
  $api.register('arithmetic', '/web', request => ({
    getOfferGuest({ sequence_type, amount, term, promo_name }) {
      const url = `/public/arithmetic/offer/stl/${sequence_type}/${amount}/${term}`;

      return request('GET', url, {
        params: { promo_name },
      });
    },
    getOfferPrivate({ amount, term, promo_name }) {
      const url = `/private/client/arithmetic/offer/stl/${amount}/${term}`;

      return request('GET', url, {
        params: { promo_name },
      });
    },
    getOfferClient({ id, sequence_type, amount, term }) {
      const params = { finance_type: 'stl', sequence_type, amount };

      params.term = term;

      return request('GET', `/private/back-office/client/${id}/arithmetic/offer`, { params });
    },
    /** Verify promoCode
     * @param {String} promoCode
     */
    verifyPromoCode(promoCode) {
      return request('PUT', '/private/client/promocode/validate', promoCode);
    },
    verifyPublicPromoCode(promoCode) {
      return request('PUT', '/public/client/promocode/validate', promoCode);
    },
   getConfig() {
      return request('GET', '/public/config/calculator');
   }
  }));

  let termOptions = {};
  let amountOptions = {};
  let initialTerm = null;
  const cacheItemPrefix = '';

  if (!store.state.arithmetic) {
     const { success, data } = await $api.dispatch('arithmetic/getConfig');
     if (!success) {
       return error('[API] failed to load configs');
     }
     const { termLimits, amountLimits, initialStates } = data;
     const { minTermForNew, maxTermForNew, minTermForRepeated, maxTermForRepeated, interval } = termLimits;
     termOptions = {
       min: {
        first: parseInt(minTermForNew, 10),
        repeated: parseInt(minTermForRepeated, 10),
       },
       max: {
        first: parseInt(maxTermForNew, 10),
        repeated: parseInt(maxTermForRepeated, 10),
       },
       interval: parseInt(interval, 10),
     };
     amountOptions = {
       min: parseInt(amountLimits.minMainLoan, 10),
       max: parseInt(amountLimits.amountTo, 10),
       first: parseInt(initialStates.new.maxAmount, 10),
       additional: parseInt(amountLimits.minAdditionLoan, 10),
       interval: parseInt(amountLimits.interval, 10),
       initial_min_new: parseInt(initialStates.new.minAmount, 10),
       initial_repeated_new: parseInt(initialStates.repeated.minAmount, 10),
     };
     if (initialStates.new.hasOwnProperty('maxTerm') && initialStates.repeated.hasOwnProperty('maxTerm')) {
       const client = store.getters['client/active'];
       initialTerm = client && client.closedLoansCount ? parseInt(initialStates.repeated.maxTerm, 10) : parseInt(initialStates.new.maxTerm, 10);
     }
  }

  const { amount, term, tab } = app.$cookies.get('calculator') || {};
  amountOptions.initial = amountOptions.first || amountOptions.max;
  if (initialTerm) {
    termOptions.initial = initialTerm;
  } else {
    termOptions.initial = typeof termOptions.max === 'object' ? termOptions.max.first : termOptions.max;
  }

  store.registerModule('arithmetic', {
    namespaced: true,
    state: () => ({
      type: 'stl',
      locked: false,
      editable: false,
      current: {
        amount: parseInt(query.amount, 10)  || amount || amountOptions.initial,
        term: parseInt(query.term, 10) || term || termOptions.initial,
        tab: tab || 0,
      },
      amount: amountOptions,
      term: termOptions,

      offers: {},
    }),
    getters: {
      /** Determine if user is authorised
       * @param {Object} state
       * @param {Object} getters
       * @param {Object} rootState
       * @param {Object} rootGetters
       * @returns {Boolean}
       */
      authorized(state, { client }, rootState, rootGetters) {
        return Boolean(rootGetters['auth/loggedIn'] && typeof client === 'object' && client.availableLoanAmount);
      },

      useTermFromApplicationOnIncrease() {
        return false
      },

      /** Get active client
       * @param {Object} state
       * @param {Object} getters
       * @param {Object} rootState
       * @param {Object} rootGetters
       */
      client(state, getters, rootState, rootGetters) {
        return rootGetters['client/active'];
      },

      application(state, getters, rootState, rootGetters) {
        return rootGetters['client/application'];
      },

      increase(state, { application }) {
        return application && application.status === $tools.status.APPLICATION_YHAT_INCREASE;
      },

      /** Determine if next loan is repeated
       * @param {*} state
       * @param {*} getters
       * @returns {Boolean}
       */
      repeated(state, { authorized, first, additional }) {
        return Boolean(authorized && !first && !additional);
      },

      /** Determine if next application is additional
       * @param {*} state
       * @param {*} getters
       * @returns {Boolean}
       */
      additional(state, { authorized, client }) {
        return Boolean(authorized && client.hasActiveLoan);
      },

      /** Determine if next loan is first (even if cancelled before)
       * @param {*} state
       * @param {*} param1
       * @param {*} param2
       * @returns {Boolean}
       */
      first(state, { authorized, client }) {
        return authorized ? !client.closedLoansCount && !client.hasActiveLoan : true;
      },
      /** Determine if next loan is first (even if cancelled or rejected before)
       *  - Business & Risk logic
       * @param {*} param1
       * @param {*} param2
       * @returns {Boolean}
       */
      firstLoan(state, { authorized, client }) {
        return authorized ? !Boolean(client.closedLoansCount) : true;
      },

      /** Determine amount thesholds for current client
       *  - Logged in client gets own values
       *  - Anonymous client gets global values
       * @param {Object} state
       * @param {Object} getters
       * @param {Object} rootState
       */
      amount(
        { amount, current: curr },
        { authorized, client, additional, increase, application }
      ) {
        const { interval, first, initial_min_new, initial_repeated_new } = amount;
        let min, max;

        if (authorized) {
          // Get max amount from client payload
          max = parseInt(client.availableLoanAmount.amount, 10);

          if (additional) {
            // Minimal amount for additional loan, if specified
            min = amount.additional;
          } else {
            min = amount.min;
          }

          if (max < min) {
            // If maximum is lower than minimum then disable
            min = max = 0;
          } else {
            // Consistently keep minimal under or equal to maximum
            min = parseInt(increase ? application.principalAmount.amount : Math.min(min, max), 10);
          }
        } else {
          ({ min, max } = amount);
        }
        // Make sure values are dividable by interval without remainder
        max = parseInt(max - (max % interval), 10);
        min = parseInt(min - (min % interval), 10);

        /** Determine current amount
         * - Return max value if current is above max
         * - Return first max value if value is 0
         * - Return less or equal to first max if first tab is selected
         * - Return actual value if no conflicts are present
         */
        const current = (() => {
          if (curr.amount > max) {
            return max;
          }

          if (curr.amount < min) {
            return min;
          }

          return curr.amount;
        })();

        return { min, max, interval, current, first, initial_min_new, initial_repeated_new };
      },

      /** Term thresholds
       * @param {Object} state
       * @param {Object} getters
       */
      term({ term, current: curr }, { type, authorized, application }) {
        let { min, max, interval } = term;

        if (typeof min === 'object') {
          min = min[type];
          max = max[type];
        }

        const useTermFromApplicationOnIncrease = false;
        const isIncrease = application.status === $tools.status.APPLICATION_YHAT_INCREASE;
        const useTermFromApplication = useTermFromApplicationOnIncrease && isIncrease && authorized;

        if (useTermFromApplication) {
          max = parseInt(application.term.value, 10);
        }

        const current = (() => {
          if (curr.term > max) {
            return max;
          }

          if (curr.term < min) {
            return min;
          }

          return curr.term;
        })();

        return { min, max, interval, current };
      },

      /** Determine active type
       * @param {*} state
       * @param {*} getters
       * @returns {String} 'first' || 'repeated' || 'client'
       */
      type({ current }, { authorized, client, first }) {
        if (authorized) {
          return first ? 'first' : 'repeated';
        }

        return current.tab === 0 ? 'first' : 'repeated';
      },

      /** Get path to offer based on current state
       * @param {*} state
       * @param {*} getters
       */
      offerKey(state, { authorized, client, type, amount, term }) {
        return getOfferKey({
          amount: amount.current,
          term: term.current,
          type,
          id: client.id,
        });
      },

      disabled({ locked, editable }, { amount, authorized, client, increase }) {
        // Locked manually or used limit
        if (locked || amount.max === 0) {
          return true;
        }

        // Has pending aplication or has refinance
        if (authorized && !increase && !editable) {
          return client.hasActualApplication || client.hasActiveRefinancing;
        }

        return false;
      },

      /** Displayed offer from current values
       * @param {*} state
       * @param {*} getters
       */
      displayed({ offers, type }, { disabled, offerKey }) {
        const payload = offers[offerKey];

        if (disabled || !payload) {
          return {};
        }

        const result = {
          serviceFee: payload.serviceFee.amount,
          withdrawalFee: payload.withdrawalFee,
          apr: payload.apr,
          principal: payload.principal,
          totalWithoutDiscount: payload.total_without_discount,
          serviceFee_without_discount: payload.serviceFee_without_discount,
          apr_without_discount: payload.apr_without_discount,
          fastFee: payload.fastFee ? payload.fastFee.amount : 0
        };

        if (type === 'loc') {
          result.interest = payload.firstEarlyRepayment.fees.interest;
          result.invoicingFee = payload.firstEarlyRepayment.fees.invoicing_fee;
          result.endDate = payload.firstEarlyRepayment.date;
          result.total = payload.firstEarlyRepayment.total;
          result.mtp = payload.schedule[0].total;
          result.firstEarlyRepaymentWithoutDiscount = payload.firstEarlyRepaymentWithoutDiscount.total
          result.scheduleWithoutDiscount = payload.scheduleWithoutDiscount[0].total
        } else {
          result.interest = payload.interest.amount;
          result.invoicingFee = payload.invoicingFee;
          result.endDate = payload.endDate;
          result.total = payload.total;
          result.processingFee = payload.processingFee;
          result.interestWithoutDiscount = payload.interest_without_discount;
          result.processingFeeWithoutDiscount = payload.processing_fee_without_discount;
          result.term = payload.term.value
        }

        result.all = payload;

        return result;
      },

      /** Displayed discount from current values
       * @param {*} state
       * @param {*} getters
       */
      discount({ offers }, { disabled, offerKey }) {
        const payload = offers[offerKey];

         if (disabled || !payload || !payload.discount) {
          return {};
        }

         return {
          interest: parseInt(payload.discount.interest, 10),
          serviceFee: parseInt(payload.discount.serviceFee, 10),
        };
      },

      /** Displayed extensions from current values
       * @param {*} state
       * @param {*} getters
       */
      extensions({ offers }, { disabled, offerKey }) {
        const payload = offers[offerKey];

        if (disabled || !payload || !payload.extensions || !payload.extensions.length) {
          return [];
        }

        return payload.extensions;
      },

      /** Current offer is loading */
      loading({ offers }, { offerKey }) {
        return !Object.prototype.hasOwnProperty.call(offers, offerKey);
      },

      /** Current offer returned an error */
      error({ offers }, { offerKey }) {
        return offers[offerKey] === false;
      },
    },
    mutations: {
      /** Universal SET mutation, only sets value that is present in state
       * @param {Object} state
       * @param {Object} payload
       * @param {String} payload.key property key
       * @param {*} payload.value new property value
       */
      SET(state, { key, value }) {
        if (key in state) state[key] = value;
      },

      /** Set current value for amount/term/tab
       * @param {Object} state
       * @param {Object} payload
       */
      setValue(state, { key, value }) {
        if (key in state.current) state.current[key] = value;
      },

      /** Add offer to calculator values
       * @param {Object} state
       * @param {Object} payload
       */
      addOffer(state, { key, value }) {
        Vue.set(state.offers, key, value);
      },
      /** Clear all offers
       * @param {Object} state
       */
      clearOffers(state) {
        state.offers = {};
      },
      /** Save promocode to state
       * @param {Object} state
       * @param {String} promocode
       */
      setPromoCode(state, promoCode) {
        state.promoCode = promoCode;
      },
    },
    actions: {
      /* Clear offers and reset calculator if promoCode is valid */
      async applyPromoCode({ commit }, { promoCode }) {
        const response = await $api.dispatch('arithmetic/verifyPromoCode', { promoCode });
        const { success, data } = response;

        if (success && data.isValid) {
          commit ('clearOffers');
          commit ('setPromoCode', promoCode);

          store.dispatch('arithmetic/getData');
        }

        return response;
      },
      async verifyPromoCode({ commit }, { promoCode }) {
        const response = await $api.dispatch('arithmetic/verifyPublicPromoCode', { promoCode });
        const { success, data } = response;

        if (success && data.isValid) {
          commit ('clearOffers');
          commit ('setPromoCode', promoCode);

          store.dispatch('arithmetic/getData');
        }

        return response;
      },
      /* Remove promoCode and reset calculator */
      clearPromoCode({ state, commit }) {
        if (state.promoCode) {
          commit('clearOffers');
          commit('setPromoCode', '');

          store.dispatch('arithmetic/getData');
        }
      },
      /* Load offer data for current values */
      getData: debounce(async ({ state, getters, commit, rootGetters }, {
        amount = getters.amount.current,
        term = getters.term.current,
      } = {}) => {
        const { client = {}, authorized, type } = getters;
        const { id } = client;
        // Construct the key manually in case values are passed to action
        const key = getOfferKey({
          amount,
          term,
          type,
          id,
        });

        // Only load each set of values once
        // Don't load values if amount equals 0
        if (amount && !state.offers[key]) {
          // Load either offer by client(admin) or own offers(client)
          const action = getters.authorized
            ? rootGetters['auth/isClient']
              ? 'getOfferPrivate' : 'getOfferClient'
            : 'getOfferGuest';
          // First or repeated loan
          const sequence_type = type;
          const payload = { id, amount, term, sequence_type, promo_name: state.promoCode };

          let value;

            const { success, data } = await $api.dispatch(`arithmetic/${action}`, payload);
            // Set false in case of error, otherwise set response data
            value = success && data;

          commit ('addOffer', { key, value });
        }
      }, 300),
      /** Update YHAT limit */
      async updateLimit({ getters, commit, dispatch }) {
        const cookieName = `limitUpdated-${getters.client.id}`;
        if (process.browser && getters.authorized && !app.$cookies.get(cookieName)) {
          commit('SET', { key: 'locked', value: true });
          const success = await dispatch('client/updateLimit', {}, { root: true });
          if (success) {
              app.$cookies.set(cookieName, true, {
                path: '/',
                maxAge: 60 * 60 * 12,
              });

            commit('SET', { key: 'locked', value: false });
          }
        }
      },
    },
  }, { preserveState: process.browser && !process.env.SPA });

  store.watch((state, getters) => getters['arithmetic/authorized'], (authorized) => {
    if (authorized) {
        store.dispatch('arithmetic/updateLimit');
    } else {
      store.commit('arithmetic/setValue', {
        key: 'amount',
        value: store.state.arithmetic.amount.first,
      });
    }
  });

  store.watch((state, getters) => getters['arithmetic/increase'], (increase) => {
    if (increase) {
      store.commit('arithmetic/setValue', {
        key: 'amount',
        value: parseInt(store.getters['arithmetic/client'].availableLoanAmount.amount, 10),
      });

      store.commit('arithmetic/setValue', {
        key: 'term',
        value: parseInt(store.getters['arithmetic/application'].term.value, 10),
      });
    }
  }, { immediate: true });

  store.watch((state) => state.arithmetic.current.tab, (tab) => {
    const first = store.state.arithmetic.amount.first;
    const current = store.getters['arithmetic/amount'].current;
    if (tab === 0 && current > first) {
      store.commit('arithmetic/setValue', {
        key: 'amount',
        value: first,
      });
    }
  });

  if (process.browser) {
    store.watch((state, getters) => getters['arithmetic/offerKey'], () => {
      const amount = store.getters['arithmetic/amount'].current;
      const term = store.getters['arithmetic/term'].current;
      const tab = store.state.arithmetic.current.tab;

      app.$cookies.set('calculator', { amount, term, tab }, {
        maxAge: 60 * 60 * 24 // 1 day
      });
    }, { immediate: true });
  }
};
